import { useToast } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { useCallback, useState } from 'react';

import {
  useExecuteValleyIbsApiRecordMutation,
  useRetryValleyIbsApiRecordMutation,
  ValleyIbsApiRecordFragmentGQLType,
} from '../../../../types/dashboard-core-gqlTypes.generated.d';
import { getErrorMessageFromApolloError } from '~/contexts/ApolloClientProvider';
import {
  GetParticipantValleyIbsApiRecordsForFundingRequestDocumentGQLType,
  GetNoteIncreaseValleyIbsApiRecordsForFundingRequestDocumentGQLType,
} from '~/packages/loans/requests/ibsIntegration.gql.generated';

export const useExecuteIbsApiRecord = () => {
  const toast = useToast();

  const [executingIbsApiId, setExecutingIbsApiId] = useState<string | null>(
    null,
  );

  // *** GQL APIs ***
  const [
    executeValleyIbsApiRecord,
    { loading: isExecutingValleyIbsApiRecord },
  ] = useExecuteValleyIbsApiRecordMutation({
    refetchQueries: [
      GetParticipantValleyIbsApiRecordsForFundingRequestDocumentGQLType,
      GetNoteIncreaseValleyIbsApiRecordsForFundingRequestDocumentGQLType,
    ],
  });
  const [
    retryValleyIbsApiRecord,
    { loading: isRetryingValleyIbsApiRecord },
  ] = useRetryValleyIbsApiRecordMutation({
    refetchQueries: [
      GetParticipantValleyIbsApiRecordsForFundingRequestDocumentGQLType,
      GetNoteIncreaseValleyIbsApiRecordsForFundingRequestDocumentGQLType,
    ],
  });

  // *** Main Function ***
  const execute = useCallback(
    async (params: {
      ibsApiRecord?: ValleyIbsApiRecordFragmentGQLType | null;
      loanId?: string;
    }) => {
      const { ibsApiRecord, loanId } = params;

      if (!ibsApiRecord) return;

      try {
        setExecutingIbsApiId(ibsApiRecord.id);

        let ibsApiResult: ValleyIbsApiRecordFragmentGQLType | undefined;

        if (ibsApiRecord.status === 'pending') {
          const { data } = await executeValleyIbsApiRecord({
            variables: {
              ibsApiRecordId: ibsApiRecord.id,
              loanId,
            },
          });
          ibsApiResult = data?.record;
        } else if (ibsApiRecord.status === 'error') {
          const { data } = await retryValleyIbsApiRecord({
            variables: {
              existingIbsApiRecordId: ibsApiRecord.id,
              loanId,
            },
          });
          ibsApiResult = data?.record;
        }
        if (!ibsApiResult) return;

        const { status, errorMessage } = ibsApiResult;
        if (status === 'executed') {
          toast({
            title: 'Successful IBS Sync',
            status: 'success',
            position: 'top',
            duration: 3000,
            isClosable: true,
          });
        } else if (status === 'error') {
          let toastMessage =
            'Error while executing IBS API. Please try again or contact Finley support';
          if (errorMessage) {
            toastMessage = `Incurred following IBS API errors: ${errorMessage}`;
          }

          toast({
            title: toastMessage,
            status: 'error',
            position: 'top',
            isClosable: true,
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        const errorMessage = getErrorMessageFromApolloError({
          error,
          defaultMessage:
            'Error syncing IBS. Please try again or contact Finley support',
        });
        toast({
          title: errorMessage,
          status: 'error',
          position: 'top',
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setExecutingIbsApiId(null);
      }
    },
    [executeValleyIbsApiRecord, retryValleyIbsApiRecord, toast],
  );

  return {
    executeIbsApi: execute,
    isExecuteIbsApiLoading:
      isExecutingValleyIbsApiRecord || isRetryingValleyIbsApiRecord,
    executingIbsApiId,
  };
};
